import { lazy, Suspense } from "react";
// These screens load with their route, not with the first screen of the app.
const InstanceAccessConsent = lazy(() => import("../../components/InstanceAccessConsent"));
const InstanceOperationConsent = lazy(() => import("../../components/InstanceOperationConsent"));
const ManagementOAuthConsent = lazy(() => import("../../components/ManagementOAuthConsent"));
const AccountSignIn = lazy(() => import("../../components/AccountSignIn"));
// Full-screen routes rendered OUTSIDE both shells (no AppShell, no
// AccountShell): sign-in/continuation callbacks and account sign-in. These are
// always available, in any mode.
import {
  readAuthRecoveryReturn,
  resolvePostAuthRoute,
  readSignInNext,
} from "../../auth/authNext";
import { announceSignedIn } from "../../auth/authRecovery";
import type { AccountAuthState } from "../../auth/accountTypes";
import AuthCallback from "../account/AuthCallback";
import ContinuationCallback from "../account/ContinuationCallback";

const STANDALONE_ROUTES = new Set([
  "/oauth/authorize",
  "/instance-consent",
  "/oauth/instance",
  "/auth/callback",
  "/continuation/callback",
  // `/sign-in` is the canonical first-party sign-in screen. The former
  // `/account` alias is retired with no redirect.
  "/sign-in",
]);

export function isStandaloneRoute(route: string): boolean {
  return STANDALONE_ROUTES.has(route);
}

export default function StandaloneRoutes({
  route,
  navigate,
  applyAccountState,
  commitSignedInSession,
  assignLocation = (url) => window.location.assign(url),
}: {
  route: string;
  navigate: (route: string) => void;
  applyAccountState: (state: AccountAuthState) => void;
  commitSignedInSession: (state: AccountAuthState) => void;
  /** Cross-origin document navigation (test seam). */
  assignLocation?: (url: string) => void;
}) {
  if (route === "/instance-consent")
    return <Suspense fallback={null}><InstanceAccessConsent navigate={navigate} /></Suspense>;
  if (route === "/oauth/instance")
    return <Suspense fallback={null}><InstanceOperationConsent navigate={navigate} /></Suspense>;
  if (route === "/oauth/authorize")
    return <Suspense fallback={null}><ManagementOAuthConsent navigate={navigate} /></Suspense>;
  if (route === "/auth/callback") {
    return (
      <div className="gallery-standalone">
        <AuthCallback
          onSessionResolved={applyAccountState}
          navigate={navigate}
        />
      </div>
    );
  }
  if (route === "/continuation/callback") {
    return (
      <div className="gallery-standalone">
        <ContinuationCallback />
      </div>
    );
  }
  if (route === "/sign-in") {
    const recoveryReturn = readAuthRecoveryReturn();
    return (
      <div className="gallery-standalone">
        <Suspense fallback={null}>
        <AccountSignIn
          onBack={() => navigate(resolvePostAuthRoute(readSignInNext(), "/"))}
          onSessionEstablished={(state) => {
            commitSignedInSession(state);
            // Other ato.run documents waiting on auth recovery re-check now.
            announceSignedIn();
            // A direct App link must resume as soon as authentication succeeds.
            // Email sign-in may offer optional passkey enrollment afterwards,
            // but that must not hold the originating App navigation hostage.
            if (state.status === "signed_in" && recoveryReturn) {
              assignLocation(recoveryReturn);
            }
          }}
          onAccountSignedIn={(state) => {
            if (state.status !== "signed_in") return;
            // App recovery already returned on session establishment, before
            // optional enrollment. Do not issue a second navigation here.
            if (recoveryReturn) {
              return;
            }
            // Email OTP and passkey both resolve IN-PAGE, so this screen owns
            // the post-sign-in hop. `next` is validated to a same-origin route;
            // any fragment on THIS url (the continuation popup's
            // `#continuation_*` hints) rides along to the target, which is how
            // `/auth/callback` still finishes a session continuation.
            const next = resolvePostAuthRoute(readSignInNext(), "/");
            if (next === "/auth/callback") {
              window.location.assign(next + window.location.hash);
              return;
            }
            navigate(next);
          }}
        />
        </Suspense>
      </div>
    );
  }
  return null;
}
